// Must use relative paths (see `Authoring Content` scripts)
import flat from 'flat';
import bgBG from './translations/bg-BG.json';
import csCZ from './translations/cs-CZ.json';
import daDK from './translations/da-DK.json';
import deAT from './translations/de-AT.json';
import deCH from './translations/de-CH.json';
import deDE from './translations/de-DE.json';
import elGR from './translations/el-GR.json';
import enBH from './translations/en-BH.json';
import enGB from './translations/en-GB.json';
import enIN from './translations/en-IN.json';
import enNG from './translations/en-NG.json';
import enPH from './translations/en-PH.json';
import enTZ from './translations/en-TZ.json';
import enUS from './translations/en-US.json';
import enVN from './translations/en-VN.json';
import srRS from './translations/sr-RS.json';
import hrHR from './translations/hr-HR.json';
import esES from './translations/es-ES.json';
import esLA from './translations/es-LA.json';
import etEE from './translations/et-EE.json';
import fiFI from './translations/fi-FI.json';
import frBE from './translations/fr-BE.json';
import frCA from './translations/fr-CA.json';
import frFR from './translations/fr-FR.json';
import huHU from './translations/hu-HU.json';
import itIT from './translations/it-IT.json';
import ltLT from './translations/lt-LT.json';
import lvLV from './translations/lv-LV.json';
import msMY from './translations/ms-MY.json';
import nbNO from './translations/nb-NO.json';
import nlBE from './translations/nl-BE.json';
import nlNL from './translations/nl-NL.json';
import plPL from './translations/pl-PL.json';
import ptBR from './translations/pt-BR.json';
import ptPT from './translations/pt-PT.json';
import roRO from './translations/ro-RO.json';
import skSK from './translations/sk-SK.json';
import svFI from './translations/sv-FI.json';
import svSE from './translations/sv-SE.json';
import thTH from './translations/th-TH.json';
import ukUA from './translations/uk-UA.json';
import viVN from './translations/vi-VN.json';
import zhHK from './translations/zh-HK.json';
import zhSG from './translations/zh-SG.json';
import defaults from '../defaults';

const languages = {
  bg_BG: bgBG,
  cs_CZ: csCZ,
  da_DK: daDK,
  de_AT: deAT,
  de_BE: deDE,
  de_CH: deCH,
  de_DE: deDE,
  el_GR: elGR,
  en_AE: enGB,
  en_AU: enGB,
  en_BE: enGB,
  en_BH: enBH,
  en_CA: enUS,
  en_CH: enGB,
  en_CZ: enGB,
  en_DE: enGB,
  en_ES: enGB,
  en_GB: enGB,
  en_HK: enGB,
  en_IE: enGB,
  en_IN: enIN,
  en_IT: enGB,
  en_JO: enGB,
  en_KE: enGB,
  en_KW: enGB,
  en_NG: enNG,
  en_NO: enGB,
  en_NZ: enGB,
  en_PH: enPH,
  en_PL: enGB,
  en_QA: enGB,
  en_SA: enGB,
  en_SE: enGB,
  en_SG: enGB,
  en_TH: enGB,
  en_TZ: enTZ,
  en_US: enUS,
  en_EG: enGB,
  en_HR: enGB,
  en_IL: enGB,
  en_RS: enGB,
  en_VN: enVN,
  es_AR: esLA,
  es_CL: esLA,
  es_CO: esLA,
  es_DO: esLA,
  es_ES: esES,
  es_EC: esLA,
  es_GT: esLA,
  es_HN: esLA,
  es_MX: esLA,
  es_NI: esLA,
  es_PA: esLA,
  es_PE: esLA,
  es_PY: esLA,
  es_SV: esLA,
  es_UY: esLA,
  et_EE: etEE,
  fi_FI: fiFI,
  fr_BE: frBE,
  fr_CA: frCA,
  fr_CH: frFR,
  fr_FR: frFR,
  hu_HU: huHU,
  it_CH: itIT,
  it_IT: itIT,
  hr_HR: hrHR,
  sr_RS: srRS,
  lt_LT: ltLT,
  lv_LV: lvLV,
  ms_MY: msMY,
  nb_NO: nbNO,
  nl_BE: nlBE,
  nl_NL: nlNL,
  pl_PL: plPL,
  pt_BR: ptBR,
  pt_PT: ptPT,
  ro_RO: roRO,
  sk_SK: skSK,
  sv_FI: svFI,
  sv_SE: svSE,
  th_TH: thTH,
  uk_UA: ukUA,
  vi_VN: viVN,
  zh_HK: zhHK,
  zh_SG: zhSG
};

/**
 * Replaces a xx-XX locale with the format: xx_XX
 */
const n = (locale: string) => locale.replace('-', '_');

let languageContent: Record<string, string> = {};

const fallbackContent = flat<Record<string, string>>(languages[n(defaults.locale)]);

/**
 * Globally sets the desired locale to use for language translations.
 *
 * @param locale - locale in the format xx-XX or xx_XX
 */
const setLocale = (locale: string = null) => {
  if (locale) {
    const content = languages[n(locale)];

    if (content) {
      languageContent = flat<Record<string, string>>(content);
    }
  }
};

/**
 * Translates the given token.
 *
 * Placeholder variables ({0}, {1}, ...) are replaced with the values having their respective
 * indices in `placeholders`.
 *
 * @param token - the i18n token to reference
 * @param [placeholders = []] - optional placeholders to replace, if expected
 * @returns translated content string
 */
const $t = (token: string, placeholders?: string[]) => {
  const content = languageContent[token] || fallbackContent[token] || token;

  if (Array.isArray(placeholders)) {
    return placeholders.reduce((str, value, index) => {
      return str.replace(`{${index}}`, value);
    }, content);
  }

  return content;
};

export default {
  setLocale,
  $t
};
